import React, { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { Controller } from 'react-hook-form';
import FormLabel from 'components/atoms/FormLabel';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import { StyledFormElementWrapper } from 'theme/styles';
import { IFormElementProps } from 'models/form';
import { getNestedErrorMessage } from './helpers';
import { StyledField } from './styles';

interface IFormNumberInput extends IFormElementProps {
  disabled?: boolean;
  arrayName?: string;
  arrayIndex?: number;
  required?: boolean;
  fieldName?: string;
  defaultValue?: any;
  withValidation?: boolean;
  withLabel?: boolean;
  max?: number;
  min?: number;
  withControllers?: boolean;
  helperText?: string;
}

export const FormNumberInput = ({
  name,
  label,
  control,
  errors,
  arrayName,
  arrayIndex,
  disabled,
  required,
  fieldName,
  defaultValue,
  withValidation = true,
  withLabel = true,
  max,
  min = 0,
  withControllers = true,
  helperText,
}: IFormNumberInput) => {
  const [error, setError] = useState<string>('');

  useEffect(() => {
    let isError = false;
    if (name.includes('.') && !name.includes('[') && Object.keys(errors).length) {
      const keys = name.split('.');
      const errorMessage = getNestedErrorMessage(errors, keys);
      if (keys && errorMessage) {
        isError = true;
        setError(errorMessage);
      }
    } else {
      const errorMessage =
        arrayName && arrayIndex !== undefined && fieldName
          ? errors?.[arrayName]?.[arrayIndex]?.[fieldName]?.message
          : errors[name]?.message;

      isError = true;
      setError(errorMessage);
    }
    if (!isError) {
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, name]);

  return (
    <StyledFormElementWrapper>
      {withLabel && (
        <FormLabel name={name} label={label} disabled={disabled} required={required} />
      )}
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue || 0}
        render={({ field }) => (
          <StyledField>
            {withControllers && (
              <button
                type="button"
                onClick={() => field.onChange(Math.max(min, (field.value || min) - 1))}
                style={{ padding: '5px 10px' }}
                disabled={disabled || field.value <= min}
              >
                −
              </button>
            )}
            <input
              id={name}
              name={name}
              type="number"
              value={field.value}
              onChange={(e) => {
                const inputVal = e.target.value;

                if (inputVal === '' || inputVal === '-') {
                  field.onChange('');
                  return;
                }

                const parsedVal = parseInt(inputVal, 10);
                if (Number.isNaN(parsedVal)) {
                  field.onChange('');
                  return;
                }

                let val = Math.max(min, parsedVal);
                if (max !== undefined) {
                  val = Math.min(max, val);
                }
                field.onChange(val);
              }}
              onBlur={() => {
                if (field.value === '' || field.value === null || field.value === undefined) {
                  field.onChange(min);
                }
              }}
              min={min}
              max={max}
              disabled={disabled}
            />
            {withControllers && (
              <button
                type="button"
                onClick={() => {
                  const newVal = (field.value || min) + 1;
                  field.onChange(max !== undefined ? Math.min(max, newVal) : newVal);
                }}
                style={{ padding: '5px 10px' }}
                disabled={disabled || (max !== undefined && field.value >= max)}
              >
                +
              </button>
            )}
          </StyledField>
        )
        }
      />
      {
        helperText && (
          <Typography variant="caption" sx={{ mt: 0.5 }}>
            {helperText}
          </Typography>
        )
      }
      {withValidation && <FormErrorLabel label={error} />}
    </StyledFormElementWrapper >
  );
};
